import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Mail, RefreshCw, Search, Trash2, Truck } from 'lucide-react';

import {
  useOrderDeskDetail,
  useOrderDeskCatalogSearch,
  useOrderDeskCustomerSearch,
  useOrderDeskSubmit,
  useOrderDeskAction,
  HOLD_REASON_LABELS,
  INTENT_LABELS,
  type OrderDeskMatchedLine,
} from '@/hooks/admin/useOrderDesk';
import { fmtMirrorDate } from '@/lib/dates';
import { cn } from '@/lib/utils';

/**
 * /admin/order-desk/:intakeId — the "Open to edit" page behind the proposal
 * email. Shows the forwarded email beside what we read out of it, lets a human
 * fix the account and the line items, then sends it through the same submit
 * path the email's Accept button uses.
 *
 * The delivery date is deliberately NOT editable: it comes from the customer's
 * delivery schedule (next route day), which is the rule for every channel.
 */

interface EditableLine {
  line_number: number;
  description: string;
  qty: number;
  catalog_item_id: string | null;
  matched_label: string | null;
  matched_sku: string | null;
  candidates: OrderDeskMatchedLine['candidates'];
}

/* -------------------- item picker -------------------- */

function ItemPicker({
  onPick,
  placeholder,
}: {
  onPick: (item: { catalog_item_id: string; label: string; sku: string | null }) => void;
  placeholder?: string;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useOrderDeskCatalogSearch(debounced);
  const items = data?.items ?? [];

  return (
    <div className="mt-1.5">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-2.5 text-brix-muted" />
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={placeholder ?? 'Search the catalog…'}
          className="w-full rounded-lg border border-brix-border bg-white pl-8 pr-2 py-1.5 text-[12px]"
        />
      </div>
      {debounced.length >= 2 && (
        <div className="mt-1 rounded-lg border border-brix-border bg-white max-h-44 overflow-y-auto">
          {isFetching && items.length === 0 ? (
            <div className="px-2.5 py-2 text-[12px] text-brix-muted">Searching…</div>
          ) : items.length === 0 ? (
            <div className="px-2.5 py-2 text-[12px] text-brix-muted">No matches.</div>
          ) : (
            items.map((it) => (
              <button
                key={it.catalog_item_id}
                type="button"
                onClick={() => {
                  onPick(it);
                  setTerm('');
                  setDebounced('');
                }}
                className="block w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-brix-surface"
              >
                <span className="font-semibold text-brix-ink">{it.label}</span>
                {it.sku && <span className="text-brix-muted"> · {it.sku}</span>}
                {it.category && <span className="text-brix-muted/70"> · {it.category}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------- account picker -------------------- */

function CustomerPicker({
  current,
  onPick,
}: {
  current: { id: string | null; name: string | null };
  onPick: (c: { id: string; name: string }) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useOrderDeskCustomerSearch(debounced);
  const customers = data?.customers ?? [];

  return (
    <div>
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-2.5 text-brix-muted" />
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={current.name ? `Change from ${current.name}…` : 'Search customers…'}
          className="w-full rounded-lg border border-brix-border bg-white pl-8 pr-2 py-1.5 text-[12px]"
        />
      </div>
      {debounced.length >= 2 && (
        <div className="mt-1 rounded-lg border border-brix-border bg-white max-h-44 overflow-y-auto">
          {isFetching && customers.length === 0 ? (
            <div className="px-2.5 py-2 text-[12px] text-brix-muted">Searching…</div>
          ) : customers.length === 0 ? (
            <div className="px-2.5 py-2 text-[12px] text-brix-muted">No matches.</div>
          ) : (
            customers.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onPick({ id: c.id, name: c.name });
                  setTerm('');
                  setDebounced('');
                }}
                className="block w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-brix-surface"
              >
                <span className="font-semibold text-brix-ink">{c.name}</span>
                {c.qbo_customer_id != null && (
                  <span className="text-brix-muted"> · QBO {c.qbo_customer_id}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------- page -------------------- */

export default function AdminOrderDeskDetail() {
  const { intakeId } = useParams<{ intakeId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useOrderDeskDetail(intakeId);

  const submit = useOrderDeskSubmit();
  const discard = useOrderDeskAction('discard');
  const reprocess = useOrderDeskAction('reprocess');

  const [customer, setCustomer] = useState<{ id: string | null; name: string | null }>({
    id: null,
    name: null,
  });
  const [locationId, setLocationId] = useState<string | null>(null);
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed the editable state once the record loads.
  useEffect(() => {
    if (!data) return;
    setCustomer({ id: data.intake.customer_id, name: data.customer_name });
    setLocationId(data.intake.customer_location_id);
    setLines(
      (data.intake.matched ?? []).map((m) => ({
        line_number: m.line_number,
        description: m.description,
        qty: m.qty,
        catalog_item_id: m.catalog_item_id,
        matched_label: m.matched_label,
        matched_sku: m.matched_sku,
        candidates: m.candidates ?? [],
      })),
    );
  }, [data]);

  const intake = data?.intake;
  const parsed = intake?.parsed ?? null;
  const decided =
    intake?.status === 'submitted' ||
    intake?.status === 'service_filed' ||
    intake?.status === 'discarded';

  const unmatched = useMemo(() => lines.filter((l) => !l.catalog_item_id).length, [lines]);
  const wantsOrder = intake?.intent === 'order' || intake?.intent === 'both';
  const canSubmit =
    !!customer.id && !decided && (wantsOrder ? lines.length > 0 && unmatched === 0 : !!parsed?.service);

  async function onSubmit() {
    if (!intakeId) return;
    setError(null);
    setNotice(null);
    try {
      const res = await submit.mutateAsync({
        intake_id: intakeId,
        customer_id: customer.id,
        customer_location_id: locationId,
        lines: wantsOrder
          ? lines
              .filter((l) => l.catalog_item_id)
              .map((l) => ({
                catalog_item_id: l.catalog_item_id!,
                qty: l.qty,
                description: l.description,
              }))
          : undefined,
      });
      const bits = [
        res.order_number ? `Order ${res.order_number} sent` : null,
        res.sf_job_id ? `SF job ${res.sf_job_id}` : null,
        res.service_request_id ? 'service call filed' : null,
      ].filter(Boolean);
      setNotice(bits.join(' · ') || 'Sent.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed.');
    }
  }

  if (isLoading) {
    return <div className="px-6 py-10 text-sm text-brix-muted">Loading…</div>;
  }
  if (!intake) {
    return (
      <div className="px-6 py-10">
        <p className="text-sm text-brix-muted">That order-desk record wasn't found.</p>
        <Link to="/admin/order-desk" className="text-sm font-semibold text-brix-primary hover:underline">
          ← Back to the order desk
        </Link>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-4 max-w-6xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => navigate('/admin/order-desk')}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brix-primary hover:underline"
        >
          <ArrowLeft size={14} /> Order desk
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={reprocess.isPending || decided}
            onClick={() => {
              if (!intakeId) return;
              setNotice('Re-reading the email — refresh in a moment.');
              void reprocess.mutateAsync(intakeId).catch(() => setError('Could not start the re-read.'));
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brix-border px-3 py-1.5 text-[12px] font-semibold text-brix-muted hover:text-brix-ink disabled:opacity-50"
          >
            <RefreshCw size={13} /> Re-read
          </button>
          <button
            type="button"
            disabled={discard.isPending || decided}
            onClick={() => {
              if (!intakeId) return;
              if (!window.confirm('Mark this as not an order? Nothing goes to Service Fusion.')) return;
              void discard
                .mutateAsync(intakeId)
                .then(() => navigate('/admin/order-desk'))
                .catch((e) => setError(e instanceof Error ? e.message : 'Failed.'));
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brix-border px-3 py-1.5 text-[12px] font-semibold text-brix-muted hover:text-red-700 disabled:opacity-50"
          >
            <Trash2 size={13} /> Not an order
          </button>
        </div>
      </div>

      {notice && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {decided && (
        <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-3 text-sm text-brix-muted">
          This record is <strong className="text-brix-ink">{intake.status.replace(/_/g, ' ')}</strong>
          {intake.reviewed_by ? ` — by ${intake.reviewed_by}` : ''}
          {intake.order_id && (
            <>
              {' · '}
              <Link to={`/orders/${intake.order_id}`} className="text-brix-primary hover:underline font-semibold">
                view the order
              </Link>
            </>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ---------------- the email ---------------- */}
        <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
          <div className="px-4 py-2.5 bg-brix-surface border-b border-brix-border">
            <h3 className="text-sm font-semibold text-brix-ink flex items-center gap-1.5">
              <Mail size={14} className="text-brix-primary" /> The forwarded email
            </h3>
          </div>
          <div className="p-4 space-y-3 text-[13px]">
            <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1">
              <dt className="text-brix-muted">Forwarded by</dt>
              <dd className="text-brix-ink font-medium">{intake.from_email ?? '—'}</dd>
              <dt className="text-brix-muted">Original sender</dt>
              <dd className="text-brix-ink font-medium">{intake.forwarded_from ?? '—'}</dd>
              <dt className="text-brix-muted">Subject</dt>
              <dd className="text-brix-ink">{intake.subject ?? '—'}</dd>
              <dt className="text-brix-muted">Read as</dt>
              <dd className="text-brix-ink">
                {intake.intent ? INTENT_LABELS[intake.intent] : '—'}
                {intake.hold_reason && !decided && (
                  <span className="text-brix-muted"> · {HOLD_REASON_LABELS[intake.hold_reason] ?? intake.hold_reason}</span>
                )}
              </dd>
            </dl>

            {intake.staff_note && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-brix-muted mb-1">
                  Note from the forwarder
                </div>
                <pre className="whitespace-pre-wrap rounded-lg bg-brix-surface p-2.5 text-[12px] text-brix-ink max-h-32 overflow-y-auto">
                  {intake.staff_note}
                </pre>
              </div>
            )}
            {intake.quoted_text && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-brix-muted mb-1">
                  The customer's message
                </div>
                <pre className="whitespace-pre-wrap rounded-lg bg-brix-surface p-2.5 text-[12px] text-brix-ink max-h-64 overflow-y-auto">
                  {intake.quoted_text}
                </pre>
              </div>
            )}
            {!intake.staff_note && !intake.quoted_text && intake.raw_text && (
              <pre className="whitespace-pre-wrap rounded-lg bg-brix-surface p-2.5 text-[12px] text-brix-ink max-h-64 overflow-y-auto">
                {intake.raw_text}
              </pre>
            )}

            {(data?.attachments ?? []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-brix-muted mb-1">Attachments</div>
                <ul className="space-y-1">
                  {(data?.attachments ?? []).map((a) => (
                    <li key={a.path}>
                      {a.url ? (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[12px] font-semibold text-brix-primary hover:underline"
                        >
                          {a.filename}
                        </a>
                      ) : (
                        <span className="text-[12px] text-brix-muted">{a.filename}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* ---------------- the proposal ---------------- */}
        <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
          <div className="px-4 py-2.5 bg-brix-surface border-b border-brix-border">
            <h3 className="text-sm font-semibold text-brix-ink">What we'd send</h3>
          </div>
          <div className="p-4 space-y-4">
            {/* account */}
            <div>
              <div className="text-[11px] uppercase tracking-wide text-brix-muted mb-1">Account</div>
              {customer.name ? (
                <div
                  className={cn(
                    'rounded-lg border px-3 py-2',
                    intake.identity_confidence === 'high'
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-amber-200 bg-amber-50',
                  )}
                >
                  <div className="font-semibold text-brix-ink text-sm">{customer.name}</div>
                  {intake.identity_note && (
                    <div className="text-[12px] text-brix-muted mt-0.5">{intake.identity_note}</div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                  {intake.identity_note ?? "We couldn't tell whose account this is."} Pick it below —
                  we'll remember this sender next time.
                </div>
              )}
              {!decided && (
                <div className="mt-2">
                  <CustomerPicker current={customer} onPick={(c) => {
                    setCustomer(c);
                    setLocationId(null);
                  }} />
                </div>
              )}
            </div>

            {/* location */}
            {(data?.locations ?? []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-brix-muted mb-1">Ship to</div>
                <select
                  disabled={decided}
                  value={locationId ?? ''}
                  onChange={(e) => setLocationId(e.target.value || null)}
                  className="w-full rounded-lg border border-brix-border bg-white px-2.5 py-1.5 text-[12px] disabled:opacity-60"
                >
                  <option value="">Use their primary location</option>
                  {(data?.locations ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {[l.display_name, l.line1, l.city].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* delivery — computed, not editable */}
            <div className="rounded-lg border border-brix-border bg-brix-surface px-3 py-2 flex items-start gap-2">
              <Truck size={14} className="text-brix-primary mt-0.5 shrink-0" />
              <div className="text-[12px]">
                {data?.schedule.date ? (
                  <>
                    <span className="font-semibold text-brix-ink">
                      Delivers {fmtMirrorDate(data.schedule.date)}
                    </span>
                    {data.schedule.note && (
                      <span className="text-brix-muted"> · {data.schedule.note}</span>
                    )}
                  </>
                ) : (
                  <span className="text-brix-muted">
                    No delivery schedule on this account — the job goes in unscheduled and ops picks
                    the day.
                  </span>
                )}
                <div className="text-brix-muted/80 mt-0.5">
                  The date comes from their delivery schedule, not the email.
                </div>
              </div>
            </div>

            {/* lines */}
            {lines.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-brix-muted mb-1.5">
                  Items {unmatched > 0 && <span className="text-amber-700">· {unmatched} to pick</span>}
                </div>
                <div className="space-y-2">
                  {lines.map((l, idx) => (
                    <div
                      key={l.line_number}
                      className={cn(
                        'rounded-lg border px-3 py-2',
                        l.catalog_item_id ? 'border-brix-border' : 'border-amber-300 bg-amber-50/40',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[12px] text-brix-muted">
                            They asked for “{l.description}”
                          </div>
                          {l.catalog_item_id ? (
                            <div className="text-sm font-semibold text-brix-ink">
                              {l.matched_label}
                              {l.matched_sku && (
                                <span className="text-brix-muted font-normal text-[12px]"> · {l.matched_sku}</span>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm font-semibold text-amber-700">Pick an item</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <input
                            type="number"
                            min={1}
                            disabled={decided}
                            value={l.qty}
                            onChange={(e) => {
                              const qty = Math.max(1, Math.round(Number(e.target.value) || 1));
                              setLines((prev) =>
                                prev.map((p, i) => (i === idx ? { ...p, qty } : p)),
                              );
                            }}
                            className="w-16 rounded-lg border border-brix-border px-2 py-1 text-[12px] text-center disabled:opacity-60"
                          />
                          {!decided && (
                            <button
                              type="button"
                              onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-brix-muted hover:text-red-700"
                              title="Remove this line"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* quick-pick alternatives from their own history */}
                      {!decided && l.candidates.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {l.candidates
                            .filter((c) => c.catalog_item_id !== l.catalog_item_id)
                            .slice(0, 3)
                            .map((c) => (
                              <button
                                key={c.catalog_item_id}
                                type="button"
                                onClick={() =>
                                  setLines((prev) =>
                                    prev.map((p, i) =>
                                      i === idx
                                        ? {
                                            ...p,
                                            catalog_item_id: c.catalog_item_id,
                                            matched_label: c.label,
                                            matched_sku: c.sku,
                                          }
                                        : p,
                                    ),
                                  )
                                }
                                className="rounded-full border border-brix-border bg-white px-2 py-0.5 text-[11px] text-brix-muted hover:text-brix-ink"
                              >
                                {c.label}
                              </button>
                            ))}
                        </div>
                      )}

                      {!decided && (
                        <ItemPicker
                          placeholder={l.catalog_item_id ? 'Change the item…' : 'Search for the item…'}
                          onPick={(item) =>
                            setLines((prev) =>
                              prev.map((p, i) =>
                                i === idx
                                  ? {
                                      ...p,
                                      catalog_item_id: item.catalog_item_id,
                                      matched_label: item.label,
                                      matched_sku: item.sku,
                                    }
                                  : p,
                              ),
                            )
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* service */}
            {parsed?.service && (
              <div className="rounded-lg border border-brix-border bg-brix-surface px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-brix-muted mb-0.5">
                  Service call we'd file
                </div>
                <div className="text-sm font-semibold text-brix-ink">{parsed.service.subject}</div>
                <div className="text-[12px] text-brix-muted">
                  {parsed.service.kind.replace(/_/g, ' ')}
                </div>
                {parsed.service.body && (
                  <p className="text-[12px] text-brix-muted mt-1 whitespace-pre-wrap">
                    {parsed.service.body}
                  </p>
                )}
              </div>
            )}

            {parsed?.notes && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                <strong>Worth a look:</strong> {parsed.notes}
              </div>
            )}

            {/* submit */}
            {!decided && (
              <div className="pt-1">
                <button
                  type="button"
                  disabled={!canSubmit || submit.isPending}
                  onClick={() => void onSubmit()}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-40"
                >
                  <Check size={15} />
                  {submit.isPending
                    ? 'Sending…'
                    : intake.intent === 'service'
                      ? 'File the service call'
                      : 'Accept & send to Service Fusion'}
                </button>
                {!canSubmit && (
                  <p className="text-[12px] text-brix-muted mt-1.5 text-center">
                    {!customer.id
                      ? 'Pick the account first.'
                      : unmatched > 0
                        ? 'Every line needs an item picked.'
                        : lines.length === 0 && !parsed?.service
                          ? 'There is nothing orderable here — mark it as not an order.'
                          : ''}
                  </p>
                )}
                <p className="text-[11px] text-brix-muted mt-2 text-center">
                  Accepting creates the order and emails the customer their confirmation.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
